import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
// import TopNavigation from './shared/TopNavigation';
import { socialLinks } from '../data/socialLinks';
import { restaurantInfo } from '../data/restaurantInfo';

const AboutPage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [visibleElements, setVisibleElements] = useState(new Set());
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    
    // Page load animation
    setTimeout(() => setIsVisible(true), 300);
    
    // Intersection Observer for scroll animations
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            setVisibleElements(prev => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    setTimeout(() => {
      const animatedElements = document.querySelectorAll('.scroll-animate');
      animatedElements.forEach(el => observer.observe(el));
    }, 500);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, []);

  return (
    <div className={`min-h-screen ${isVisible ? 'animate-page-fade-in' : 'opacity-0'}`} style={{ background: 'transparent' }}>
      {/* Top Navigation - Use Shared Component */}
      {/* <TopNavigation /> - moved to App.js */}

      {/* Hero Section */}
      <section className="relative pb-20 overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center scroll-animate" data-animate="hero">
            <h1 className="text-3xl md:text-4xl lg:text-5xl mb-6" style={{ fontFamily: 'DM Serif Display, serif', color: '#0a0a0a' }}>
              {t('aboutPage.about')}
              <span className="block italic" style={{ color: '#2F2F2F' }}>
                {t('aboutPage.sunsari')}
              </span>
            </h1>
            <p className="text-lg md:text-xl max-w-3xl mx-auto leading-relaxed" style={{ color: 'rgba(10,10,10,0.7)' }}>
              {t('aboutPage.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Text Content */}
            <div className="space-y-8 scroll-animate" data-animate="story-text">
              <div className="space-y-6">
                <span className="text-[#2F2F2F] text-sm font-light tracking-[0.3em] uppercase">{t('aboutPage.ourJourney')}</span>
                <h2 className="text-2xl md:text-3xl lg:text-4xl text-[#0a0a0a] leading-tight" style={{ fontFamily: 'DM Serif Display, serif' }}>
                  {t('aboutPage.since2019')}
                </h2>
                <p className="text-[#0a0a0a]/70 text-base leading-relaxed">
                  {t('aboutPage.journeyDescription')}
                </p>
                <p className="text-[#0a0a0a]/70 text-base leading-relaxed">
                  With 7+ years of experience, our team creates extraordinary dining experiences using fresh ingredients and traditional recipes.
                </p>
              </div>
            </div>

            {/* Image */}
            <div className="relative scroll-animate" data-animate="story-image">
              <div className="relative overflow-hidden rounded-2xl shadow-2xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_multilingual-menu-2/artifacts/33sq3pct_FB_IMG_1759419261154.jpg"
                  alt="Sunsari Restaurant Interior"
                  className="w-full h-96 lg:h-[500px] object-cover transform hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2F2F2F]/30 via-transparent to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-[#2F2F2F]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 scroll-animate" data-animate="stats-header">
            <h2 className="text-2xl md:text-3xl text-[#0a0a0a] mb-4" style={{ fontFamily: 'DM Serif Display, serif' }}>
              Our <span className="italic text-[#2F2F2F]">Achievements</span>
            </h2>
            <p className="text-[#0a0a0a]/60 text-lg">Numbers that tell our story</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { number: '7+', label: 'Years Experience', color: 'text-[#2F2F2F]' },
              { number: '10000+', label: 'Loyal Customers', color: 'text-[#2F2F2F]' },
              { number: '50+', label: 'Signature Dishes', color: 'text-[#C97A2B]' },
              { number: '1', label: 'Passion', color: 'text-[#2F2F2F]' }
            ].map((stat, index) => (
              <div key={index} className="text-center group cursor-pointer scroll-animate" data-animate={`stat-${index}`}>
                <div className={`text-4xl md:text-5xl font-bold ${stat.color} mb-2 group-hover:scale-125 transition-transform duration-500`} style={{ fontFamily: 'DM Serif Display, serif' }}>
                  {stat.number}
                </div>
                <div className="text-[#0a0a0a]/60 text-sm tracking-wide uppercase">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Restaurant Gallery Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 scroll-animate" data-animate="gallery-header">
            <h2 className="text-2xl md:text-3xl text-[#0a0a0a] mb-4" style={{ fontFamily: 'DM Serif Display, serif' }}>
              Our <span className="italic text-[#2F2F2F]">Restaurant</span>
            </h2>
            <p className="text-[#0a0a0a]/60 text-lg">Experience the authentic ambiance and culinary artistry</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Photo 3 */}
            <div className="relative overflow-hidden rounded-2xl shadow-xl group scroll-animate" data-animate="gallery-photo-1">
              <img 
                src="https://customer-assets.emergentagent.com/job_multilingual-menu-2/artifacts/snbpli86_FB_IMG_1759419208267.jpg"
                alt="Sunsari Restaurant Ambiance"
                className="w-full h-96 object-cover transform group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2F2F2F]/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </div>

            {/* Photo 4 */}
            <div className="relative overflow-hidden rounded-2xl shadow-xl group scroll-animate" data-animate="gallery-photo-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_multilingual-menu-2/artifacts/37uxt3qr_FB_IMG_1759419179909.jpg"
                alt="Sunsari Culinary Experience"
                className="w-full h-96 object-cover transform group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2F2F2F]/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </div>
          </div>
        </div>
      </section>

      {/* Master Chef Section - Moved from Homepage */}
      <section className="py-20 bg-[#2F2F2F]/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Short & Nice Description - No Chef Photos */}
          <div className="text-center max-w-4xl mx-auto scroll-animate" data-animate="about-description">
            <div className="bg-white/50 rounded-2xl p-10 border border-[#2F2F2F]/20">
              <h3 className="text-2xl md:text-3xl text-[#0a0a0a] mb-6" style={{ fontFamily: 'DM Serif Display, serif' }}>
                Culinary <span className="italic text-[#2F2F2F]">Mastery</span>
              </h3>
              <p className="text-[#0a0a0a]/70 text-base leading-relaxed">
                Our skilled team creates authentic dishes with traditional recipes and modern techniques. Every meal delivers exceptional flavors and artistic presentation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="scroll-animate" data-animate="mission">
            <h2 className="text-2xl md:text-3xl text-[#0a0a0a] mb-6" style={{ fontFamily: 'DM Serif Display, serif' }}>
              Our <span className="italic text-[#2F2F2F]">Mission</span>
            </h2>
            <p className="text-lg text-[#0a0a0a]/70 max-w-3xl mx-auto leading-relaxed">
              Creating exceptional dining experiences with authentic flavors and memorable hospitality.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#2F2F2F]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="scroll-animate" data-animate="cta">
            <h2 className="text-2xl md:text-3xl text-white mb-6" style={{ fontFamily: 'DM Serif Display, serif' }}>
              Ready to <span className="italic text-[#C97A2B]">Experience</span> Sunsari?
            </h2>
            <p className="text-lg text-white/70 mb-8">
              Join us for an unforgettable dining experience
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <button
                onClick={() => navigate('/combine-tables')}
                className="group relative px-12 py-4 bg-[#C97A2B] text-black font-bold text-lg transition-all duration-300 ease-out hover:scale-105 hover:bg-white"
                data-testid="about-reserve-btn"
              >
                <span className="relative z-10 tracking-wide flex items-center uppercase text-sm">
                  Reserve Your Table
                  <svg className="w-5 h-5 ml-3 transform group-hover:translate-x-1 transition-transform duration-300" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </span>
              </button>
              
              <button
                onClick={() => navigate('/menu')}
                className="group px-12 py-4 border border-white/30 text-white font-bold text-lg transition-all duration-300 ease-out hover:scale-105 hover:bg-white/10 uppercase text-sm"
              >
                View Our Menu
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;